import React from 'react';
import { useLanguage } from '../i18n';

export default function SubscriptionWarningModal({
  open,
  onClose,
  onLogin,
  onRegister,
  onContinueWithoutRegistration,
  onContactUs
}) {
  const { t } = useLanguage();

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div
        className="modal-content subscription-warning-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-title"
      >
        <div className="modal-header">
          <h2 id="subscription-title">{t('subscriptionRequired')}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          {/* Info banner */}
          <div className="info-alert" role="note">
            <p className="info-text">
              {t('subscriptionMessage')}
            </p>
          </div>

          {/* Login/Register block with group border */}
          <div className="action-group">
            <div className="action-grid">
              <div className="action-card">
                <h3 className="action-title">{t('haveAccountHeading')}</h3>
                <p className="action-description">{t('haveAccountDesc')}</p>
                <button
                  className="btn btn-login"
                  onClick={onLogin}
                >
                  {t('login')}
                </button>
              </div>

              <div className="action-card">
                <h3 className="action-title">{t('newAccountHeading')}</h3>
                <p className="action-description">{t('newAccountDesc')}</p>
                <button
                  className="btn btn-register"
                  onClick={onRegister}
                >
                  {t('register')}
                </button>
              </div>
            </div>
          </div>

          {/* Contact section text */}
          <div className="contact-section">
            <h3 className="action-title">{t('contactUsHeading')}</h3>
            <p className="action-description">{t('contactUsDesc')}</p>
          </div>
        </div>

        {/* Sticky footer with actions */}
        <div className="modal-footer sticky-footer" role="group" aria-label="Subscription actions">
          <button
            className="btn btn-contact"
            onClick={onContactUs}
          >
            {t('contactForSubscription')}
          </button>

          <button
            className="btn btn-continue"
            onClick={onContinueWithoutRegistration}
          >
            {t('continueWithoutRegistration')}
          </button>
        </div>
      </div>

      <style>{`
        /* Container */
        .subscription-warning-modal {
          max-width: 720px;
          width: 92%;
          padding-bottom: 0; /* footer has own padding */
          display: flex;
          flex-direction: column;
          max-height: 90vh;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #e9ecef;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 22px;
          font-weight: 700;
          color: #1f2937;
        }

        .modal-close {
          border: none;
          background: transparent;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          color: #6b7280;
        }

        .modal-body {
          padding: 16px 20px 24px 20px;
          overflow: auto;
        }

        /* Info banner */
        .info-alert {
          margin: 4px 0 20px 0;
          padding: 16px 18px;
          background: #f0f7ff;
          border: 1px solid #e0efff;
          border-left: 4px solid #0d6efd;
          border-radius: 8px;
        }

        .info-text {
          margin: 0;
          font-size: 15px;
          line-height: 1.6;
          color: #374151;
        }

        /* Action group (box around both columns) */
        .action-group {
          border: 1px solid #e9ecef;
          border-radius: 10px;
          background: #fff;
          padding: 16px;
          margin: 0 0 20px 0;
        }

        .action-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          align-items: stretch; /* gör båda kolumnerna lika höga */
        }

        .action-card {
          display: flex;
          flex-direction: column;
          gap: 10px;
          height: 100%; /* fyll höjden så knappar kan linjera i botten */
        }

        .action-title {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
        }

        .action-description {
          margin: 0;
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
        }

        /* Gör att Login & Registrera-knappar hamnar på samma höjd */
        .action-card .btn-login,
        .action-card .btn-register {
          margin-top: auto;   /* tryck knappen till botten av kortet */
          width: 100%;        /* samma bredd på båda */
        }

        /* Contact section text */
        .contact-section {
          margin-top: 10px;
          padding-top: 16px;
          border-top: 1px solid #e9ecef;
        }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 42px;
          padding: 10px 22px;
          border: 1px solid transparent;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          text-decoration: none;
          user-select: none;
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
        }

        .btn-login {
          background: #0d6efd;
          border-color: #0d6efd;
          color: #fff;
        }
        .btn-login:hover { background: #0b5ed7; border-color: #0b5ed7; }

        .btn-register {
          background: #198754;
          border-color: #198754;
          color: #fff;
        }
        .btn-register:hover { background: #157347; border-color: #157347; }

        .btn-contact {
          background: #6c757d;
          border-color: #6c757d;
          color: #fff;
        }
        .btn-contact:hover { background: #545b62; border-color: #545b62; }

        .btn-continue {
          background: #e9ecef;
          color: #495057;
          border: 1px solid #ced4da;
        }
        .btn-continue:hover {
          background: #dfe3e6;
          color: #3a4046;
          border-color: #c3c9cf;
        }

        /* Sticky footer actions */
        .modal-footer.sticky-footer {
          position: sticky;
          bottom: 0;
          background: #fff;
          border-top: 1px solid #e9ecef;
          padding: 12px 20px;
          display: flex;
          gap: 16px;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 -6px 16px rgba(0,0,0,0.05);
          z-index: 2;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .subscription-warning-modal {
            max-width: 95%;
          }

          .action-grid {
            grid-template-columns: 1fr;
          }

          .modal-footer.sticky-footer {
            flex-direction: column;
            align-items: stretch;
          }

          .modal-footer .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}